import type { WrpfCurrentRecord, WrpfRecordCategory, WrpfRecordJurisdiction } from "../handoff/types";

// Etiquetas de Récords. Espejo de `apps/nextjs/src/utils/constants.ts` del repo web
// (wrpf-argentina-app): si una etiqueta cambia allá, se cambia acá. El orden de cada lista es
// también el orden en que se muestran las opciones de los selectores.

type LabeledValue<Value extends string> = { readonly value: Value; readonly label: string };

/** `recordCategories` de la web. */
export const RECORD_CATEGORY_LABELS: ReadonlyArray<LabeledValue<WrpfRecordCategory>> = [
  { value: "REGIONAL", label: "Regionales" },
  { value: "NATIONAL", label: "Nacionales" },
  { value: "IBEROAMERICAN", label: "Iberoamericanos" },
  { value: "WORLD", label: "Mundiales" },
];

/** `regions` de la web para las regionales; el resto, con la etiqueta de su categoría. */
const JURISDICTION_LABELS: ReadonlyArray<LabeledValue<WrpfRecordJurisdiction>> = [
  { value: "NOA", label: "Región Noroeste (NOA)" },
  { value: "NEA", label: "Región Noreste (NEA)" },
  { value: "CUYO", label: "Región del Cuyo" },
  { value: "PAMPEANA", label: "Región Pampeana" },
  { value: "PATAGONICA", label: "Región Patagónica" },
  { value: "NATIONAL", label: "Nacionales" },
  { value: "IBEROAMERICAN", label: "Iberoamericanos" },
  { value: "WORLD", label: "Mundiales" },
  // Ya no es una jurisdicción editable en la web; puede venir en snapshots viejos.
  { value: "INTERNATIONAL", label: "Internacional (legacy)" },
];

/** No está en `constants.ts`: la web usa estos textos en sus formularios de récords y tandas. */
const SEX_LABELS: ReadonlyArray<LabeledValue<string>> = [
  { value: "MAN", label: "Masculino" },
  { value: "WOMAN", label: "Femenino" },
  { value: "MX", label: "Mixto" },
];

/** `divisions` de la web. */
const DIVISION_LABELS: ReadonlyArray<LabeledValue<string>> = [
  { value: "TEEN", label: "Teens (14 - 16)" },
  { value: "SUBJUNIOR", label: "Subjunior (17 - 19)" },
  { value: "JUNIOR", label: "Junior (20 - 23)" },
  { value: "OPEN", label: "Open" },
  { value: "MASTER1", label: "Master 1 (40 - 49)" },
  { value: "MASTER2", label: "Master 2 (50 - 59)" },
  { value: "MASTER3", label: "Master 3 (60 - 69)" },
  { value: "MASTER4", label: "Master 4 (70 - 79)" },
  { value: "MASTER5", label: "Master 5 (80+)" },
];

/** `weightClasses` de la web. Las etiquetas se repiten entre sexos: se eligen después del sexo. */
const WEIGHT_CLASS_LABELS: ReadonlyArray<LabeledValue<string>> = [
  { value: "F_CAT44", label: "-44kg" },
  { value: "F_CAT48", label: "-48kg" },
  { value: "F_CAT52", label: "-52kg" },
  { value: "F_CAT56", label: "-56kg" },
  { value: "F_CAT60", label: "-60kg" },
  { value: "F_CAT67", label: "-67.5kg" },
  { value: "F_CAT75", label: "-75kg" },
  { value: "F_CAT82", label: "-82.5kg" },
  { value: "F_CAT90", label: "-90kg" },
  { value: "F_CATHW", label: "+90kg" },
  { value: "M_CAT52", label: "-52kg" },
  { value: "M_CAT56", label: "-56kg" },
  { value: "M_CAT60", label: "-60kg" },
  { value: "M_CAT67", label: "-67.5kg" },
  { value: "M_CAT75", label: "-75kg" },
  { value: "M_CAT82", label: "-82.5kg" },
  { value: "M_CAT90", label: "-90kg" },
  { value: "M_CAT100", label: "-100kg" },
  { value: "M_CAT110", label: "-110kg" },
  { value: "M_CAT125", label: "-125kg" },
  { value: "M_CAT140", label: "-140kg" },
  { value: "M_CATHW", label: "+140kg" },
];

/** `liftTypes` de la web (disciplina del récord). */
const DISCIPLINE_LABELS: ReadonlyArray<LabeledValue<WrpfCurrentRecord["discipline"]>> = [
  { value: "SBD", label: "Full Powerlifting" },
  { value: "S", label: "Squat Only" },
  { value: "BD", label: "Push-Pull" },
  { value: "B", label: "Bench Only" },
  { value: "D", label: "Deadlift Only" },
];

/** `equipmentTypes` de la web. */
const EQUIPMENT_LABELS: ReadonlyArray<LabeledValue<WrpfCurrentRecord["equipment"]>> = [
  { value: "RAW", label: "RAW" },
  { value: "CLASSIC", label: "Classic RAW" },
  { value: "SINGLE_PLY", label: "Single-Ply" },
  { value: "MULTI_PLY", label: "Multi-Ply" },
];

/** No está en `constants.ts`: nombres de levantamiento definidos por el fork (spec v0.30). */
const LIFT_LABELS: ReadonlyArray<LabeledValue<WrpfCurrentRecord["liftType"]>> = [
  { value: "SQUAT", label: "Sentadilla" },
  { value: "BENCH_PRESS", label: "Press banca" },
  { value: "DEADLIFT", label: "Peso muerto" },
  { value: "TOTAL", label: "Total" },
];

export type RecordField = "jurisdiction" | "sex" | "division" | "weightClass" | "discipline" | "equipment" | "liftType";

const LABELS_BY_FIELD: Readonly<Record<RecordField, ReadonlyArray<LabeledValue<string>>>> = {
  jurisdiction: JURISDICTION_LABELS,
  sex: SEX_LABELS,
  division: DIVISION_LABELS,
  weightClass: WEIGHT_CLASS_LABELS,
  discipline: DISCIPLINE_LABELS,
  equipment: EQUIPMENT_LABELS,
  liftType: LIFT_LABELS,
};

/** Etiqueta de un valor del récord; si la web agrega un valor que el espejo no conoce, se muestra crudo. */
export const recordFieldLabel = (field: RecordField, value: string): string =>
  LABELS_BY_FIELD[field].find((candidate) => candidate.value === value)?.label ?? value;

export const recordCategoryLabel = (category: WrpfRecordCategory): string =>
  RECORD_CATEGORY_LABELS.find((candidate) => candidate.value === category)?.label ?? category;

/** Ordena valores según la lista de la web; los desconocidos van al final, alfabéticamente. */
export const sortRecordFieldValues = (field: RecordField, values: ReadonlyArray<string>): string[] => {
  const order = LABELS_BY_FIELD[field].map((candidate) => candidate.value);
  const rank = (value: string): number => {
    const index = order.indexOf(value);
    return index === -1 ? order.length : index;
  };
  return [...values].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
};
